import { Button, ProgressBar, SegmentedControl, cn } from '@ai-tutor/ui';
import {
  BREATHING_PATTERNS,
  REST_SCALE,
  type BreathingPatternId,
} from './breathingPatterns';

export type BreathingStatus = 'idle' | 'running' | 'done';
export type BreathingDuration = '1' | '2';

const PATTERN_OPTIONS = [
  { value: 'cyclic', label: 'Cyclic sigh' },
  { value: 'box', label: 'Box' },
] as const;

const DURATION_OPTIONS = [
  { value: '1', label: '1 min' },
  { value: '2', label: '2 min' },
] as const;

export interface BreathingViewProps {
  pattern: BreathingPatternId;
  onPatternChange: (pattern: BreathingPatternId) => void;
  duration: BreathingDuration;
  onDurationChange: (duration: BreathingDuration) => void;
  status: BreathingStatus;
  /** The current instruction while running. */
  cue?: string;
  /** Circle size this step eases to, and how long it takes to get there. */
  scale?: number;
  stepSeconds?: number;
  /** 0–1 through the current step; drives the reduced-motion bar. */
  stepProgress?: number;
  remainingLabel?: string;
  reducedMotion?: boolean;
  onStart: () => void;
  onStop: () => void;
  /** Inside the break sheet: no pattern and length pickers. */
  compact?: boolean;
}

const CIRCLE_SIZE = {
  compact: 'h-20 w-20',
  default: 'h-40 w-40',
} as const;

function BreathCircle({
  scale,
  stepSeconds,
  reducedMotion,
  size,
}: {
  scale: number;
  stepSeconds: number;
  reducedMotion: boolean;
  size: keyof typeof CIRCLE_SIZE;
}) {
  return (
    <div
      aria-hidden='true'
      className={cn(
        'relative mx-auto flex items-center justify-center',
        CIRCLE_SIZE[size]
      )}
    >
      <div className='absolute inset-0 rounded-full border border-border' />
      <div
        className='h-full w-full rounded-full bg-brand-300/50 ring-1 ring-inset ring-brand-400/60 dark:bg-tint-brand-strong dark:ring-brand-400/40'
        // The step's own length IS the transition: the circle arrives at its
        // size exactly when the cue changes. Static when motion is reduced.
        style={
          reducedMotion
            ? { transform: 'scale(0.7)' }
            : {
                transform: `scale(${scale})`,
                transitionProperty: 'transform',
                transitionDuration: `${stepSeconds}s`,
                transitionTimingFunction: 'cubic-bezier(0.37, 0, 0.63, 1)',
              }
        }
      />
    </div>
  );
}

function BreathingControls({
  pattern,
  onPatternChange,
  duration,
  onDurationChange,
}: Pick<
  BreathingViewProps,
  'pattern' | 'onPatternChange' | 'duration' | 'onDurationChange'
>) {
  return (
    <div className='flex flex-wrap items-center justify-center gap-2'>
      <SegmentedControl
        segmentClassName='tap-target'
        label='Breathing technique'
        size='sm'
        options={PATTERN_OPTIONS}
        value={pattern}
        onChange={onPatternChange}
      />
      <SegmentedControl
        segmentClassName='tap-target'
        label='Length'
        size='sm'
        options={DURATION_OPTIONS}
        value={duration}
        onChange={onDurationChange}
      />
    </div>
  );
}

const BUTTON_LABEL: Record<BreathingStatus, string> = {
  idle: 'Start',
  running: 'Stop',
  done: 'Go again',
};

/** The instruction while running; the technique, or a word after, at rest. */
function BreathingCaption({
  status,
  cue,
  description,
  remainingLabel,
}: {
  status: BreathingStatus;
  cue?: string;
  description: string;
  remainingLabel?: string;
}) {
  return (
    <div className='min-h-[3rem]' aria-live='polite'>
      {status === 'running' && (
        <>
          <p className='text-lg font-semibold text-foreground'>{cue}</p>
          {remainingLabel && (
            <p className='font-mono text-xs text-muted-foreground'>
              {remainingLabel} left
            </p>
          )}
        </>
      )}
      {status === 'done' && (
        <p className='text-sm font-medium text-foreground'>
          Nicely done. Notice how you feel now.
        </p>
      )}
      {status === 'idle' && (
        <p className='text-sm text-muted-foreground'>{description}</p>
      )}
    </div>
  );
}

/**
 * The breathing exercise, drawn from props. `BreathingExercise` owns the
 * clock; this only shows where in the pattern the session is.
 */
export function BreathingView(props: BreathingViewProps) {
  const {
    pattern,
    status,
    cue,
    scale = REST_SCALE,
    stepSeconds = 0.4,
    stepProgress = 0,
    remainingLabel,
    reducedMotion = false,
    compact = false,
  } = props;
  const running = status === 'running';
  // At rest the circle sits small and still; running, it eases per step.
  const circle = running
    ? { scale, stepSeconds }
    : { scale: REST_SCALE, stepSeconds: 0.4 };

  return (
    <div className={cn('text-center', compact ? 'space-y-3' : 'space-y-4')}>
      <BreathCircle
        scale={circle.scale}
        stepSeconds={circle.stepSeconds}
        reducedMotion={reducedMotion}
        size={compact ? 'compact' : 'default'}
      />
      <BreathingCaption
        status={status}
        cue={cue}
        description={BREATHING_PATTERNS[pattern].description}
        remainingLabel={remainingLabel}
      />
      {running && reducedMotion && (
        <ProgressBar
          value={Math.round(stepProgress * 100)}
          size='sm'
          ariaLabel='Progress through this breath'
        />
      )}
      {!compact && !running && <BreathingControls {...props} />}
      <Button
        variant={running ? 'outline' : 'default'}
        size='sm'
        className='max-sm:h-11'
        onClick={running ? props.onStop : props.onStart}
      >
        {BUTTON_LABEL[status]}
      </Button>
    </div>
  );
}
